<!--
  ChatScreen.vue - チャット画面コンポーネント
  このコンポーネントは地図作成AIとのチャット機能を提供します。
  主な機能：
  - WebSocketを使用したリアルタイムチャット
  - 地図スタイル選択（スタンダード/シンプル）
  - AIバージョン選択（version1/version2）
  - 地図共有・保存機能
  - メッセージの折り畳み表示
  - IME入力対応
-->
<template>
  <!-- チャット画面のメインコンテナ -->
  <div class="chat-container">
    <!-- チャットエリア - メッセージ表示と地図選択 -->
    <div class="chat-area">
      <!-- メッセージ表示エリア -->
      <div class="messages" ref="messages" :class="{ isLoading: isLoading }">
        <!-- 各メッセージの表示 -->
        <div v-for="(message, index) in messages" :key="index" class="message">
          <div :class="['message-content', message.role === 'Agent' ? 'assistant-text' : 'user-text']">
            <!-- メッセージの内容を表示 -->
            <div :class="['message-text', { 'collapsed': message.isCollapsed && message.shouldCollapse }]">
              <pre v-if="message.role === 'Agent'" class="message-pre">{{ message.content }}</pre>
              <pre v-else class="message-pre">{{ message.content }}</pre>
            </div>

            <!-- 折り畳みボタン（長いメッセージの場合のみ表示） -->
            <div v-if="message.shouldCollapse" class="collapse-button" @click="toggleCollapse(index)">
              <span v-if="message.isCollapsed">▼ 展開</span>
              <span v-else>▲ 折り畳み</span>
            </div>
          </div>
        </div>

        <!-- 地図選択・JSON確認用のUI -->
        <div class="message">
          <!-- 地図スタイル選択画面 -->
          <div v-if="isMapSelect === 0 && !idFlg" :class="['message', 'message-content', 'assistant-text']">
            <p>ベースの地図を選んでください</p>
            <!-- 地図選択グリッド -->
            <div class="map-grid">
              <div v-for="(map, index) in maps" :key="index" class="map-option" :class="{ selected: selectedMap === index }" @click="selectMap(index)">
                <img :src="map.image" :alt="map.name" class="map-image" />
                <p class="map-name">{{ map.name }}</p>
              </div>
            </div>
            <!-- 地図選択確定ボタン -->
            <div class="button-container">
              <button class="start-button" @click="startWithSelectedMap">
                この地図で始める
              </button>
            </div>
          </div>

          <!-- AI提案の確認ダイアログ -->
          <div v-if="isJsonIncluded" :class="['message', 'message-content', 'assistant-text']">
            <p>この変更を適用しますか？</p>
            <button class="chat-deside">
              <button class="start-button" @click="handleButtonClick(true)">
                はい
              </button>
              <button class="start-button" @click="handleButtonClick(false)">
                いいえ
              </button>
            </button>
          </div>
        </div>
      </div>

      <!-- ローディング表示 -->
      <div v-if="isLoading" class="message-content assistant-loading">{{ initialResponse }}</div>
      <Loading :isVisible="isLoading" :fullScreen="false" />
    </div>

    <!-- チャット入力エリア -->
    <div class="chat-input-container">
      <!-- アクションボタン群 -->
      <div class="action-buttons">
        <button class="action-button reset-button" @click="shareMap()">
          <img :src="shareIcon" alt="共有" class="action-icon" />
        </button>
        <button class="action-button share-button" @click="saveMap()">
          <img :src="saveIcon" alt="保存" class="action-icon" />
        </button>
      </div>

      <!-- チャット入力とAI選択 -->
      <div class="chat-input-wrapper">
        <!-- AIバージョン選択メニュー -->
        <div class="ai-version-selector">
          <img
            :src="require('@/assets/btn_AI.png')"
            alt="AI選択"
            class="ai-select-button"
            @click="toggleAiVersionMenu"
          />
          <!-- AIバージョン選択メニュー -->
          <div v-if="showAiVersionMenu" class="ai-version-menu">
            <div
              class="ai-version-option"
              :class="{ selected: selectedAiVersion === 'version2' }"
              @click="selectAiVersion('version2')"
            >
              <div class="version-label">version2</div>
              <div class="version-description">入力したコメントを補完するAI</div>
            </div>
            <div
              class="ai-version-option"
              :class="{ selected: selectedAiVersion === 'version1' }"
              @click="selectAiVersion('version1')"
            >
              <div class="version-label">version1</div>
              <div class="version-description">スタンダードなAI</div>
            </div>
          </div>
        </div>

        <!-- チャット入力テキストエリア -->
        <textarea
          :value="value"
          @input="updateValue"
          class="chat-input"
          :readonly="isJsonIncluded || isMapSelect!=2"
          :placeholder="currentPlaceholder"
          @keydown.enter="handleEnter"
          @compositionstart="isComposing = true"
          @compositionend="handleCompositionEnd"
        ></textarea>

        <!-- 送信ボタン -->
        <button class="send-button" @click="sendMessage">
          <img :src="sendIcon" alt="送信" class="send-icon" />
        </button>
      </div>
    </div>

    <!-- 商用利用案内リンク -->
    <div class="custom-link">
      <a href="https://www.zenrin-datacom.net/solution/areacutter" target="_blank" rel="noopener noreferrer" class="link-text">
        地図作成ツール「AreaCutter」についてはこちら
        <img src="@/assets/arrow.png" alt="矢印" class="arrow" />
      </a>
      <a href="https://www.zenrin-datacom.net/solution/zenrin-maps-api/design_map_ai/consul" target="_blank" rel="noopener noreferrer" class="link-text">
        地図のAIチャット機能に関するご相談はこちら
        <img src="@/assets/arrow.png" alt="矢印" class="arrow" />
      </a>
    </div>
  </div>
</template>

<script>
/**
 * ChatScreen.vue コンポーネント
 * 地図作成AIとのチャット機能を提供するコンポーネント
 * WebSocketを使用したリアルタイムチャット、地図スタイル選択、
 * AIバージョン選択、地図共有・保存機能を実装
 */
import { EventBus } from '@/views/eventBus.js'
import fcc from './fcc.json'
import Loading from '@/components/Loading.vue'
import config from '@/config'
import axios from 'axios'

export default {
  name: 'ChatScreen',

  // 親コンポーネントから受け取るプロパティ
  props: {
    mapid: {
      type: String,
      required: true
    },
    idFlg: {
      type: Boolean,
      required: false
    },
    zoomlevel: {
      type: Number,
      required: true
    },
    mapshareurl: {
      type: String,
      required: false
    },
    value: {
      type: String,
      required: false
    }
  },

  // 使用するコンポーネント
  components: {
    Loading
  },
  data () {
    return {
      // チャット関連
      messages: [], // チャットメッセージの配列
      ws: null, // WebSocket接続インスタンス
      sender: 'User', // 送信者名

      // UI状態管理
      isJsonIncluded: false, // AI提案のJSONが含まれているかどうか
      isMapSelect: 0, // 地図選択状態（0: 未選択, 2: 選択済み）
      selectedMap: 0, // 選択された地図のインデックス
      isLoading: false, // ローディング状態
      isinit: true, // 初期化フラグ

      // プレースホルダー
      placeholders: [
        '公園を青で表示して',
        '全体的に桜のようなピンク色の地図にして',
        '駅を強調表示して',
        '高速道路を消して'
      ],
      currentPlaceholder: '', // 現在表示中のプレースホルダー

      // アイコン・アセット
      sendIcon: require('@/assets/btn_send.png'),
      shareIcon: require('@/assets/btn_share.png'),
      saveIcon: require('@/assets/btn_save.png'),

      // WebSocket関連
      connectionTimer: null, // 接続タイマー
      initialResponse: '…', // 初期応答メッセージ

      // IME入力制御
      isComposing: false, // IME入力中かどうかを判定
      compositionEnded: false, // IME変換終了直後のEnterを無視するためのフラグ

      // システム設定
      hostname: config.domain, // ホスト名
      shorturl: config.shorturl, // 短縮URL

      // AIバージョン選択
      showAiVersionMenu: false, // AIバージョン選択メニューの表示状態
      selectedAiVersion: 'version2' // 選択されたAIバージョン
    }
  },
  mounted () {
    // 既存地図の場合は地図選択をスキップ
    if (this.idFlg) {
      this.isinit = true
      this.isMapSelect = 2
    }
    // WebSocket接続とプレースホルダー設定
    this.initializeWebSocket()
    this.changePlaceholder()
  },
  computed: {
    /**
     * 地図スタイルの選択肢を動的に生成
     * kidsイベントの場合は専用の地図スタイルを返す
     */
    maps () {
      // kidsイベントの場合、シンプルを白地図に変更
      if (this.$route.query.event === 'kids') {
        return [
          { name: 'スタンダード', image: require('@/assets/gmap_kids.png') },
          { name: 'シンプル', image: require('@/assets/white_kids.png') }
        ]
      }
      // 通常の場合
      return [
        { name: 'スタンダード', image: require('@/assets/standard_map.png') },
        { name: 'シンプル', image: require('@/assets/simple_map.png') }
      ]
    }
  },

  beforeDestroy () {
    // コンポーネント破棄時にタイマーをクリア
    clearTimeout(this.connectionTimer)
  },
  methods: {
    /**
     * 入力値の更新を親コンポーネントに通知
     * @param {Event} event - 入力イベント
     */
    updateValue (event) {
      this.$emit('input', event.target.value)
    },

    /**
     * プレースホルダーをランダムに変更
     * ユーザーにサンプル入力を提案
     */
    changePlaceholder () {
      const randomIndex = Math.floor(Math.random() * this.placeholders.length)
      this.currentPlaceholder = this.placeholders[randomIndex]
    },

    /**
     * AI提案の確認ボタンクリック時の処理
     * @param {boolean} update - 提案を適用するかどうか
     */
    handleButtonClick (update) {
      let changemessage = ''
      if (update === false) {
        EventBus.$emit('changeMapDesign', 'back')
        changemessage = '変更を取り消しました'
        this.addMessage('Agent', changemessage)
      } else {
        EventBus.$emit('changeMapDesign', 'ok')
        changemessage = '変更を適用しました'
        this.addMessage('Agent', changemessage)
      }

      // 変更結果をWebSocketで送信
      const sendData = {
        action: 'saveMessage',
        channelId: this.mapid,
        message: changemessage,
        sender: 'Agent'
      }
      this.ws.send(JSON.stringify(sendData))
      this.isJsonIncluded = false
      this.$emit('input', '') // textareaの値をクリア
    },

    /**
     * 地図スタイルの選択処理
     * @param {number} index - 選択された地図のインデックス
     */
    selectMap (index) {
      this.selectedMap = index
      EventBus.$emit('changeMapDesign', index)
    },

    /**
     * 選択された地図で開始する処理
     * 地図選択状態を完了に設定
     */
    startWithSelectedMap () {
      const mapName = this.maps[this.selectedMap].name
      this.isMapSelect = 2
      this.addMessage('Agent', 'ベース地図を' + mapName + 'に設定しました')
    },

    /**
     * 応答メッセージを追加
     * @param {string} response - 応答メッセージ
     */
    addResponseMessage (response) {
      this.messages.push({ role: 'assistant', contentt: response })
    },
    /**
     * WebSocket接続を初期化
     * チャットサーバーとのリアルタイム通信を確立
     */
    initializeWebSocket () {
      this.ws = new WebSocket(config.chatApi.websocket + `/chat/?channelId=${this.mapid}`)

      this.ws.onopen = () => {
        if (this.isinit) {
          this.loadMessages()
          // 1時間後に接続を切断（タイムアウト処理）
          this.connectionTimer = setTimeout(() => {
            this.ws.close()
            this.addMessage('Agent', 'チャットがタイムアウトしました。続ける場合は保存ボタンを押下後、編集用URLに遷移してください。最初から始める場合は画面をリロードしてください。')
          }, 3600000) // 1時間後に切断
          this.isinit = false
        }
      }

      this.ws.onmessage = (event) => {
        const message = JSON.parse(event.data)
        if (message.Message !== '' && message.Message !== null) {
          if (message.Sender === 'Agent' && message.Type === 'initialResponse') {
            this.initialResponse = message.Message
          } else {
            this.addMessage(message.Sender, message.Message)
          }
        }
        if (message.Sender === 'Agent' && message.Type === 'finalResponse') {
          if (!message.Designs && !message.Font) {
            this.isJsonIncluded = false
          } else {
            EventBus.$emit('changeMapDesign', message)

            if (message.Designs) {
              const zoomValues = this.getUniqueZoomValues(message.Designs)

              if (!zoomValues.includes(this.zoomlevel)) {
                // 現在の縮尺にマッチしない場合
                this.addMessage(message.Sender, '地図を拡大するか縮小して確認してください。')
              }
            }
            this.isJsonIncluded = true
          }
          this.isLoading = false
        }
      }

      this.ws.onerror = (error) => {
        this.log('WebSocket Error:')
        this.log(error)
      }

      this.ws.onclose = () => {
        setTimeout(() => {
          this.initializeWebSocket()
        }, 1000)
      }
    },

    /**
     * AI提案のズームレベル値を抽出
     * @param {Object} res - AIからのレスポンスデータ
     * @returns {Array} ユニークなズームレベルの配列
     */
    getUniqueZoomValues (res) {
      const zoomSet = new Set()
      // A内のpolyとlineの全レイヤを処理
      const layerTypes = ['poly', 'line', 'note']
      layerTypes.forEach((layerType) => {
        // レイヤータイプが存在するか確認してから処理
        if (res[layerType] && Array.isArray(res[layerType])) {
          res[layerType].forEach(([layerName]) => {
            // 変数Bと一致するレイヤ名称を検索
            const matchingLayer = fcc.find((b) => b.fcc === layerName.replace(/^DCN:/, ''))
            if (matchingLayer) {
              // 一致する場合、その"zoom"値をSetに追加
              matchingLayer.scale.forEach((z) => zoomSet.add(z))
            }
          })
        }
      })

      // Setからユニークな値を配列として返す
      return Array.from(zoomSet).sort((a, b) => a - b)
    },

    /**
     * Enterキー押下時の処理
     * IME変換中は無視し、Shift+Enterで改行、Enterで送信
     * @param {Event} event - キーボードイベント
     */
    handleEnter (event) {
      if (this.isComposing || this.compositionEnded) return
      if (!event.shiftKey) {
        // ShiftキーなしでEnterを押した場合、送信
        event.preventDefault()
        this.sendMessage()
      }
    },

    /**
     * IME変換終了時の処理
     * 変換直後のEnterキーを無視するための制御
     */
    handleCompositionEnd () {
      this.isComposing = false
      this.compositionEnded = true
      setTimeout(() => {
        this.compositionEnded = false // すぐにリセット
      }, 50)
    },

    /**
     * メッセージを送信
     * 選択されたAIバージョンでチャットメッセージを送信
     */
    sendMessage () {
      if (this.value.trim()) {
        // // URLパラメータai=novaの場合、actionを変更
        // let action = this.selectedAiVersion
        // if (this.$route.query.ai === 'nova') {
        //   if (this.selectedAiVersion === 'version1') {
        //     action = 'chatMessage1'
        //   } else if (this.selectedAiVersion === 'version2') {
        //     action = 'chatMessage3'
        //   }
        // }
        // console.log(action)
        const sendData = {
          action: this.selectedAiVersion,
          channelId: this.mapid,
          message: this.value.trim(),
          sender: this.sender
        }
        this.ws.send(JSON.stringify(sendData))
        this.addMessage(this.sender, this.value.trim())
        this.value = '' // 入力フィールドをクリア
        this.isLoading = true
        this.changePlaceholder()
        // dcnTextを初期化
        EventBus.$emit('resetDcnText')
      }
    },

    /**
     * 過去のメッセージを読み込み
     * チャット履歴を復元
     */
    async loadMessages () {
      try {
        const response = await fetch('https://' + config.chatApi.domain + `/prod/${this.mapid}/messages`)
        const data = await response.json()
        data.forEach((message) => {
          if (message.Message !== '' && message.Message !== null) {
            this.addMessage(message.Sender, message.Message)
          }
        })
      } catch (error) {
        this.log('メッセージ取得Error:')
        this.log(error)
      }
    },

    /**
     * メッセージをチャット画面に追加
     * 長いメッセージは折り畳み表示に対応
     * @param {string} sender - 送信者名
     * @param {string} message - メッセージ内容
     */
    addMessage (sender, message) {
      if (typeof sender !== 'undefined' && typeof message !== 'undefined') {
        // メッセージの長さを判定（文字数で判定）
        const shouldCollapse = message.length > 200 // 200文字以上で折り畳み対象

        this.messages.push({
          role: sender,
          content: message,
          shouldCollapse: shouldCollapse,
          isCollapsed: shouldCollapse // デフォルトで折り畳み
        })
        this.$nextTick(() => {
          const chatContainer = this.$refs.messages
          chatContainer.scrollTop = chatContainer.scrollHeight
        })
      }
    },

    /**
     * チャンネルをリセット
     * チャット履歴を削除
     */
    async resetChannel () {
      try {
        const response = await fetch(`https://o2dzwsi7xh.execute-api.us-east-1.amazonaws.com/prod/${this.mapid}/delete`, {
          method: 'DELETE'
        })
        if (!response.ok) throw new Error('Network response was not ok')
        this.$refs.messages.innerHTML = '' // メッセージ表示エリアをクリア
      } catch (error) {
        this.log('Error:')
        this.log(error)
      }
    },

    /**
     * ログ出力
     * @param {*} obj - 出力対象のオブジェクト
     */
    log (obj) {
      console.log(obj)
    },

    /**
     * オブジェクトを文字列に変換
     * @param {*} obj - 変換対象のオブジェクト
     * @returns {string} 文字列化されたオブジェクト
     */
    toString (obj) {
      return typeof obj === 'object' ? JSON.stringify(obj, null, 2) : String(obj)
    },

    /**
     * 地図を共有
     * 短縮URLの生成とネイティブ共有機能の利用
     */
    async shareMap () {
      EventBus.$emit('changeMapDesign', 'shareMap')
      const today = new Date()
      const after30Days = new Date()
      after30Days.setDate(today.getDate() + 30)
      const dateOnly = after30Days.toISOString().split('T')[0]

      if (this.shorturl !== 'null') {
        await axios.post('/s/', 'https://' + this.hostname + '/design_map_ai/mapshare?mapid=' + this.mapid, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }).then((response) => {
          this.mapshareurl = response.data.url
        }).catch((error) => {
          console.log('エラー内容:', error)
        })
      } else {
        this.mapshareurl = 'https://' + this.hostname + '/design_map_ai/mapshare?mapid=' + this.mapid
      }

      // 現在のURLをクリップボードにコピー
      if (typeof window !== 'undefined' && navigator.share) {
        try {
          await navigator.share({
            title: '有効期限：' + dateOnly + ' | デザイン地図AI',
            text: '有効期限：' + dateOnly + '　| デザイン地図AI',
            url: this.mapshareurl
          })
        } catch (error) {
          console.error('共有がキャンセルされました', error)
        }
      } else {
        alert('このブラウザは共有機能に対応していません')
      }
    },

    /**
     * 地図を保存
     * EventBusを通じて保存処理を実行
     */
    saveMap () {
      // 現在のURLをクリップボードにコピー
      EventBus.$emit('changeMapDesign', 'saveMap')
    },

    /**
     * AIバージョン選択メニューの表示/非表示を切り替え
     */
    toggleAiVersionMenu () {
      this.showAiVersionMenu = !this.showAiVersionMenu
    },

    /**
     * AIバージョンを選択
     * @param {string} version - 選択されたAIバージョン
     */
    selectAiVersion (version) {
      this.selectedAiVersion = version
      this.showAiVersionMenu = false
    },

    /**
     * メッセージの折り畳み状態を切り替え
     * @param {number} index - メッセージのインデックス
     */
    toggleCollapse (index) {
      this.messages[index].isCollapsed = !this.messages[index].isCollapsed
    }
  }
}
</script>

<style scoped>
/* ===== チャット画面のレイアウト ===== */

/* チャットコンテナ - 全体のレイアウト */
.chat-container {
  height: calc(100% - 43px);
}

/* チャットエリア - メッセージ表示と地図選択 */
.chat-area {
  flex: 1;
  padding: 10px;
}

/* ===== メッセージ表示 ===== */

/* メッセージ表示エリア */
.messages {
  margin: 5px 0;
  border-radius: 5px;
  font-size: 14px;
  overflow: auto;
  height: calc(100vh - 260px);
}

/* ローディング中のメッセージエリア */
.messages.isLoading {
  height: calc(100vh - 370px);
}

/* スクロールバーのスタイル */
.messages::-webkit-scrollbar {
  width: 5px;
}

.messages::-webkit-scrollbar-thumb {
  border-radius: 8px;
}

/* メッセージの基本スタイル */
.message {
  padding: 5px;
}

/* メッセージコンテンツの基本スタイル */
.message-content {
  border-radius: 13px;
  width: fit-content;
  max-width: 90%;
  padding: 15px;
  text-align: left; /* メッセージ内の文字を左詰め */
}

/* AI（アシスタント）メッセージのスタイル */
.message-content.assistant-text {
  text-align: left;
  margin-right: auto;
  background: #E1E3EF;
}

/* ユーザーメッセージのスタイル */
.message-content.user-text {
  text-align: left;
  margin-left: auto;
  background: #F1EAE6;
}

/* ローディング表示用のスタイル */
.message-content.assistant-loading {
  text-align: left;
  background: #E1E3EF;
  margin: 0 auto;  /* 左右中央配置 */
  font-size: 13px;
}

/* メッセージテキストの基本スタイル */
.message-text {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* メッセージのpre要素スタイル */
.message-pre {
  font-family: inherit;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  margin: 0;
  padding: 0;
  background: transparent;
  border: none;
  font-size: inherit;
  line-height: inherit;
  color: inherit;
}

/* 折り畳まれたメッセージのスタイル */
.message-text.collapsed {
  max-height: 100px;
  overflow: hidden;
  position: relative;
}

/* 折り畳まれたメッセージのグラデーション効果 */
.message-text.collapsed::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 30px;
  background: linear-gradient(transparent, rgba(225, 227, 239, 0.9));
  pointer-events: none;
}

/* ユーザーメッセージ用のグラデーション */
.message-content.user-text .message-text.collapsed::after {
  background: linear-gradient(transparent, rgba(241, 234, 230, 0.9));
}

/* 折り畳みボタンのスタイル */
.collapse-button {
  margin-top: 8px;
  padding: 4px 8px;
  background: rgba(109, 135, 193, 0.1);
  border: 1px solid rgba(109, 135, 193, 0.3);
  border-radius: 12px;
  cursor: pointer;
  font-size: 12px;
  color: #6D87C1;
  text-align: center;
  transition: background-color 0.2s;
  user-select: none;
}

.collapse-button:hover {
  background: rgba(109, 135, 193, 0.2);
}

.collapse-button span {
  display: inline-block;
  vertical-align: middle;
}

/* ===== 地図選択UI ===== */

/* 地図選択の決定ボタン群 */
.chat-deside {
  display: flex;        /* 横並びにする */
  align-items: center;  /* 縦方向の中央揃え */
  gap: 15px;            /* 画像間の間隔を15pxに設定 */
  padding: 0;           /* 余白を削除 */
  cursor: pointer;      /* ポインターをクリック可能に */
}

/* 地図選択グリッド */
.map-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  justify-content: center;
  margin-bottom: 20px;
}

/* 地図選択オプション */
.map-option {
  border: 4px solid transparent;
  cursor: pointer;
  padding: 10px;
  background-color: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, border-color 0.2s;
}

.map-option.selected {
  border-color: #6D87C1;
  border-radius: 4px;
}

.map-option:hover {
  transform: scale(1.05);
}

/* 地図画像 */
.map-image {
  width: 100%;
  height: auto;
  border-radius: 4px;
}

/* 地図名 */
.map-name {
  margin-bottom: 0px;
  font-size: 14px;
  font-weight: bold;
  text-align: center;
}

/* ボタンコンテナ */
.button-container {
  display: flex;
  justify-content: center;
}

/* 開始ボタン */
.start-button {
  background-color: #6D87C1;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 10px 40px;
  cursor: pointer;
  transition: background-color 0.3s;
}

/* ===== チャット入力エリア ===== */

/* チャット入力コンテナ */
.chat-input-container {
  position: fixed;
  bottom: 60px;
  width: 100%;
  max-width: 480px;
  padding: 10px 10px;
  display: flex;
  align-items: center;
  gap: 10px;  /* ボタン間の間隔 */
}

/* チャット入力ラッパー */
.chat-input-wrapper {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 800px;           /* 中央配置用の最大幅 */
  background-color: white;  /* 入力欄の背景色 */
  border: 3px solid #ccc;   /* 枠線 */
  border-radius: 12px;        /* 丸み */
  padding: 1px 5px;
  box-sizing: border-box;
  gap: 10px;                  /* ボタン間の間隔を追加 */
  height: 90px;
  margin-right: 5px;
}

/* ===== AIバージョン選択 ===== */

/* AIバージョン選択セレクター */
.ai-version-selector {
  position: absolute;
  bottom: 55px;
  right: 22px;
  z-index: 5;
}

/* AI選択ボタン */
.ai-select-button {
  /* width: 30px;
  height: 30px; */
  cursor: pointer;
  transition: transform 0.2s;
}

.ai-version-menu {
  position: absolute;
  bottom: 50px;
  right: 0;
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 10;
  width: 200px;
  padding: 10px 0;
  margin-top: 5px;
}

/* AIバージョン選択オプション */
.ai-version-option {
  padding: 10px 15px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 5px;
  transition: background-color 0.2s;
}

.ai-version-option:hover {
  background-color: #f0f0f0;
}

.ai-version-option.selected {
  background-color: #FFE4E4;
  font-weight: bold;
}

/* バージョンラベル */
.version-label {
  font-size: 14px;
  font-weight: bold;
}

/* バージョン説明 */
.version-description {
  font-size: 12px;
  color: #666;
}

/* ===== チャット入力フィールド ===== */

/* チャット入力テキストエリア */
.chat-input {
  flex: 1;
  border: none;
  outline: none;
  resize: none; /* リサイズ無効化 */
  padding: 10px;
  font-size: 16px;
  line-height: 1.5;
  border-radius: 20px;
  height: auto;
  max-height: 150px;  /* 入力欄の最大高さ */
  overflow-y: auto;   /* 必要に応じてスクロール表示 */
}

.chat-input::placeholder {
  color: #aaa;  /* プレースホルダーの色 */
}

/* ===== ボタン類 ===== */

/* 送信ボタン */
.send-button {
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  top: 17px;
}

/* リセットボタン */
.reset-button {
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  align-items: center;
  position: relative;
}

/* 共有ボタン */
.share-button {
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  align-items: center;
  position: relative;
}

/* 送信アイコン */
.send-icon {
  width: 34px;
}

/* ===== その他のUI要素 ===== */

/* 商用利用案内リンク */
.custom-link {
  background-color: #E2DADA; /* 背景色 */
  padding: 7px 7px;
  display: flex;
  flex-direction: column;
  position: fixed;
  align-items: center;
  bottom: 0;
  width: 100%;
  max-width: 480px;
  justify-content: center;
  gap: 5px;
}

.link-text {
  color: #2B48A8;
  font-size: 14px;
}

.arrow {
  margin-left: 5px;
  position: relative;
  top: 2px;
}

/* アクションボタン群 */
.action-buttons {
  display: flex;
  flex-direction: column;
}

.action-icon {
  width: 34px;
}
</style>
